import React, { FC, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import AuthContext from '../../../contexts/authContext';
import ThemeContext from '../../../contexts/themeContext';
import usePermission from '../../../hooks/usePermission';
import authService from '../../../services/authService';
import introService from '../../../services/introService';
import Portal from '../../../layout/Portal/Portal';
import './IntroTourGate.scss';

interface IIntroStep {
	key: string;
	label: string;
	description: string;
	tabs: string[];
}

interface ISpotlightRect {
	top: number;
	left: number;
	width: number;
	height: number;
	right: number;
	bottom: number;
}

const descriptions: Record<string, string> = {
	dashboard: 'See the production, stock, pending work and recent activity available to your role.',
	profile: 'Update your contact details, password and profile photo.',
	production: 'Track garments from fabric receipt and cutting through bundles, process work and ready stock.',
	sales: 'Manage customer invoices, receipts, returns and credit balances.',
	purchase: 'Manage supplier invoices, payments, returns and supplier balances.',
	finance: 'Record expenses and review your business cash and bank activity.',
	master: 'Set up the shared lists used in production, sales and purchasing.',
	user_management: 'Manage user accounts, roles and the access available to each role.',
	fabric_inward: 'Record fabric received from suppliers and track quantities by lot.',
	fabric_stock: 'Check remaining fabric by lot in taka and metres.',
	cutting: 'Record fabric usage, cutting layers and pieces produced by size.',
	cutting_stock: 'Review cut components available for bundling and the next production steps.',
	bundling: 'Combine cut components into traceable bundles for production.',
	bundle_stock: 'Check bundle quantities and their current production stage.',
	process_issue: 'Assign bundles to a worker and process, then track issued and pending quantities.',
	process_receive: 'Record received quantities as good, damaged, short or altered.',
	process_flow: 'Track bundles across their assigned processes and find where work is pending.',
	ready_stock: 'Review finished garments available for customer dispatch.',
	delivery_challan: 'Prepare delivery challans and record goods dispatched to customers.',
	labour_hisaab: 'Review worker earnings, advances, payments and outstanding balances.',
	sales_invoice: 'Create customer invoices with item quantities, tax and due dates.',
	customer_payment: 'Record customer payments and apply them to outstanding invoices or credits.',
	credit_note: 'Record customer returns or sales adjustments and track the resulting customer credit.',
	purchase_invoice: 'Record supplier purchases with item quantities, tax and due dates.',
	supplier_payment: 'Record supplier payments and apply available debit-note credits to supplier balances.',
	debit_note: 'Record supplier returns or purchase adjustments and track the resulting supplier credit.',
	expenses: 'Record business expenses with supporting notes and attachments.',
	petty_cash: 'Track small cash balances and day-to-day cash transactions.',
	bank: 'Manage bank accounts and review deposits, withdrawals and balances.',
	item: 'Maintain garment styles, including their colours, sizes and production routing.',
	item_type: 'Group garment styles by type for easier selection and reporting.',
	size: 'Maintain the sizes used on styles, cutting plans and production records.',
	colour: 'Maintain garment colours used in item and stock records.',
	process: 'Configure the production operations used by your workshop.',
	fabric: 'Maintain the fabrics used by your business and production recipes.',
	fabric_type: 'Classify fabric by the material or construction used in your operation.',
	fabric_stage: 'Define the stages used to track fabric through your workflow.',
	cutting_component: 'Define garment parts used in cutting, such as the front, back, sleeve and collar.',
	labour: 'Maintain worker profiles and the processes each worker can handle.',
	party: 'Maintain customer and supplier records used in transactions.',
	vendor: 'Maintain suppliers and service providers used in purchases and expenses.',
	expense_category: 'Organize expenses into categories used by your business.',
	tax_rate: 'Configure the GST rates and tax components used on business documents.',
	users: 'Create and manage staff and labour accounts under the authority hierarchy.',
	roles: 'Set up roles and choose which screens and actions each role can use.',
	role: 'Set up roles and choose which screens and actions each role can use.',
	company: 'Review organization and branch details used by business records.',
	branch: 'Work with records assigned to the selected branch.',
	general_setting: 'Review organization-wide settings that affect day-to-day operations.',
	login_history: 'Review account sign-ins and session activity.',
	reports: 'Open the reports available to your role and review operational results.',
	reports_dashboard: 'See an overview of the reports available to your role.',
	report_production_summary: 'Compare issued, completed, damaged and pending quantities by process.',
	report_fabric_stock: 'Review fabric inward, usage and balance by lot.',
	report_cutting: 'Review cutting output, usage and production records.',
	report_cutting_variance: 'Find cutting records where actual fabric use differs from the planned layers.',
	report_cutting_stock: 'Review cut component quantities available for downstream work.',
	report_bundle_tracking: 'Trace bundle movement through workers and production processes.',
	report_process_pending: 'Find work still pending with workers or processes.',
	report_ready_stock: 'Review completed goods available for dispatch.',
	report_dispatch: 'Review delivery and dispatch activity.',
	report_lot_trace: 'Trace material and work history from lot through finished goods.',
	report_labour_payment: 'Review worker earnings, payment and unpaid balances.',
};

const groupDescriptions: Record<string, string> = {
	business: 'Manage customer sales and supplier purchases, record payments, and track business expenses.',
	setting: 'Set up the shared items, fabrics, processes, tax rates and other lists used across your workspace.',
	production: 'Track garments from fabric receipt and cutting through process work, finished stock and dispatch.',
	reports: 'Review production, stock, dispatch and labour results through the reports available to your role.',
	user_management: 'Manage user accounts, assign roles and control which screens and actions each role can access.',
};

const normalize = (value: unknown) => String(value || '').toLowerCase().trim().replace(/[\s&/]+/g, '_');

const describe = (key: string, label: string) =>
	descriptions[normalize(key)] ||
	descriptions[normalize(label)] ||
	(normalize(key).startsWith('process_work_')
		? `Complete bundles assigned to ${label} and record the good, damaged, short or altered quantity.`
		: 'Use this area to manage the related records and workflow available to your role.');

const visibleTabLabels = (node: Record<string, any>): string[] => {
	const labels: string[] = [];
	const visit = (items: Record<string, any> | null | undefined) => {
		Object.values(items || {}).forEach((item: any) => {
			if (!item || typeof item !== 'object') return;
			if (item.subMenu && Object.keys(item.subMenu).length) {
				visit(item.subMenu);
			} else if (item.text) {
				labels.push(String(item.text));
			}
		});
	};
	if (node.subMenu && Object.keys(node.subMenu).length) visit(node.subMenu);
	else if (node.text) labels.push(String(node.text));
	return labels;
};

const buildSteps = (menu: Record<string, any>): IIntroStep[] => {
	const groups: Record<string, { label: string; targetKey: string; nodes: Array<[string, any]> }> = {};
	Object.entries(menu || {}).forEach(([key, node]) => {
		if (!node || typeof node !== 'object' || String(node.id || '').startsWith('sidebar-title-')) return;
		const sidebarSection = String(node.sectionLabel || '').trim();
		const groupKey = sidebarSection ? `section:${sidebarSection}` : `menu:${key}`;
		const group = groups[groupKey] || {
			label: sidebarSection || String(node.text || 'Your workspace'),
			targetKey: key,
			nodes: [],
		};
		group.nodes.push([key, node]);
		groups[groupKey] = group;
	});

	return Object.keys(groups).map((groupKey) => {
		const group = groups[groupKey];
		const tabs: string[] = [];
		const descriptionsForGroup: string[] = [];
		group.nodes.forEach(([key, node]) => {
			visibleTabLabels(node).forEach((label) => {
				if (!tabs.includes(label)) tabs.push(label);
			});
			descriptionsForGroup.push(describe(key, String(node.text || group.label)));
		});
		const description =
			groupDescriptions[normalize(group.label)] ||
			(group.nodes.length > 1
				? `This area brings together ${tabs.join(', ')}. ${descriptionsForGroup.join(' ')}`
				: descriptionsForGroup[0]);
		return { key: group.targetKey, label: group.label, description, tabs };
	});
};

const IntroTourGate: FC = () => {
	const { authUser, isLoading: isAuthLoading } = useContext(AuthContext);
	const { asideStatus, setAsideStatus } = useContext(ThemeContext);
	const { dynamicMenu, isLoadingPermissions } = usePermission();
	const [isOpen, setIsOpen] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [stepIndex, setStepIndex] = useState(0);
	const [spotlight, setSpotlight] = useState<ISpotlightRect | null>(null);
	const [isTourPositioned, setIsTourPositioned] = useState(false);
	const [popoverPosition, setPopoverPosition] = useState({ top: 24, left: 24 });
	const checkedUserRef = useRef<number | null>(null);
	const previousAsideStatusRef = useRef<boolean | null>(null);
	const popoverRef = useRef<HTMLDivElement>(null);
	const authUserId = Number(authUser?.id || 0);
	const steps = useMemo(() => buildSteps(dynamicMenu || {}), [dynamicMenu]);
	const displayName = authUser?.name || authUser?.username || 'there';
	const roleName = authUser?.role?.name || 'your role';
	const activeStep = steps[stepIndex];
	const finishIntro = useCallback(async () => {
		if (isSaving) return;
		setIsSaving(true);
		setIsOpen(false);
		try {
			await introService.complete();
		} catch {
			// Persist the dismissal for this browser session if the API is offline.
			sessionStorage.setItem(`stichmaster_intro_dismissed_${authUserId}`, '1');
		} finally {
			setIsSaving(false);
		}
	}, [authUserId, isSaving]);
	const skipTour = () => {
		finishIntro().catch(() => undefined);
	};

	useEffect(() => {
		let active = true;
		if (!authUserId) {
			checkedUserRef.current = null;
			setIsOpen(false);
		} else if (
			!isAuthLoading &&
			!isLoadingPermissions &&
			authService.isAuthenticated() &&
			checkedUserRef.current !== authUserId
		) {
			checkedUserRef.current = authUserId;
			const sessionKey = `stichmaster_intro_dismissed_${authUserId}`;
			if (!sessionStorage.getItem(sessionKey)) {
				introService
					.getStatus()
					.then((completed) => {
						if (active && !completed) {
							window.dispatchEvent(new Event('stichmaster:clear-menu-search'));
							setStepIndex(0);
							setIsOpen(true);
						}
					})
					.catch(() => {
						// Let users view and dismiss the guide even if its status API is temporarily unavailable.
						if (active) {
							window.dispatchEvent(new Event('stichmaster:clear-menu-search'));
							setStepIndex(0);
							setIsOpen(true);
						}
					});
			}
		}
		return () => {
			active = false;
		};
	}, [authUserId, isAuthLoading, isLoadingPermissions]);

	useEffect(() => {
		const reopen = () => {
			if (authUserId && authService.isAuthenticated()) {
				window.dispatchEvent(new Event('stichmaster:clear-menu-search'));
				setStepIndex(0);
				setIsOpen(true);
			}
		};
		window.addEventListener('stichmaster:replay-intro', reopen);
		return () => window.removeEventListener('stichmaster:replay-intro', reopen);
	}, [authUserId]);

	useEffect(() => {
		let timeout: number;
		let frame = 0;
		let cancelled = false;
		const findTarget = () => {
			const elements = document.querySelectorAll<HTMLElement>('[data-tour-menu-key]');
			for (let index = 0; index < elements.length; index += 1) {
				const element = elements.item(index);
				if (element.dataset.tourMenuKey === activeStep?.key) return element;
			}
			return null;
		};
		const updateRect = () => {
			const target = findTarget();
			if (!target) return;
			const rect = target.getBoundingClientRect();
			setSpotlight({
				top: Math.max(4, rect.top - 5),
				left: Math.max(4, rect.left - 5),
				width: rect.width + 10,
				height: rect.height + 10,
				right: rect.left + rect.width + 5,
				bottom: rect.top + rect.height + 5,
			});
		};
		const locateTarget = (attempt = 0) => {
			if (cancelled) return;
			const target = findTarget();
			if (!target) {
				if (attempt < 8) timeout = window.setTimeout(() => locateTarget(attempt + 1), 100);
				else setIsTourPositioned(true);
				return;
			}
			target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
			timeout = window.setTimeout(() => {
				updateRect();
				setIsTourPositioned(true);
			}, 180);
		};
		const handleScrollOrResize = () => {
			if (frame) window.cancelAnimationFrame(frame);
			frame = window.requestAnimationFrame(updateRect);
		};

		if (isOpen) {
			if (previousAsideStatusRef.current === null) previousAsideStatusRef.current = asideStatus;
			setAsideStatus(true);
			if (activeStep) locateTarget();
			else setIsTourPositioned(true);
			window.addEventListener('resize', handleScrollOrResize);
			window.addEventListener('scroll', handleScrollOrResize, true);
		} else {
			setSpotlight(null);
			setIsTourPositioned(false);
			if (previousAsideStatusRef.current !== null) {
				setAsideStatus(previousAsideStatusRef.current);
				previousAsideStatusRef.current = null;
			}
		}
		return () => {
			cancelled = true;
			window.clearTimeout(timeout);
			if (frame) window.cancelAnimationFrame(frame);
			window.removeEventListener('resize', handleScrollOrResize);
			window.removeEventListener('scroll', handleScrollOrResize, true);
		};
	}, [activeStep, asideStatus, isOpen, setAsideStatus]);

	useLayoutEffect(() => {
		if (!isOpen || !popoverRef.current) return;
		const panel = popoverRef.current.getBoundingClientRect();
		const viewportWidth = window.innerWidth;
		const viewportHeight = window.innerHeight;
		let left = spotlight ? spotlight.left + spotlight.width + 18 : (viewportWidth - panel.width) / 2;
		let top = spotlight ? spotlight.top : (viewportHeight - panel.height) / 2;
		if (viewportWidth < 760) {
			left = 16;
			const spaceBelow = viewportHeight - (spotlight?.bottom || 0) - 14;
			const spaceAbove = (spotlight?.top || 0) - 14;
			if (spotlight && spaceBelow < panel.height && spaceAbove > spaceBelow) {
				top = Math.max(16, spotlight.top - panel.height - 14);
			} else {
				top = (spotlight?.bottom || 0) + 14;
			}
		} else if (left + panel.width > viewportWidth - 16) {
			left = Math.max(16, (spotlight?.left || 0) - panel.width - 18);
		}
		left = Math.max(16, Math.min(left, viewportWidth - panel.width - 16));
		top = Math.max(16, Math.min(top, viewportHeight - panel.height - 16));
		setPopoverPosition({ top, left });
	}, [isOpen, spotlight, stepIndex, steps.length]);

	useEffect(() => {
		const handleEscape = (event: KeyboardEvent) => {
			if (event.key === 'Escape') finishIntro().catch(() => undefined);
		};
		if (isOpen) window.addEventListener('keydown', handleEscape);
		return () => window.removeEventListener('keydown', handleEscape);
	}, [finishIntro, isOpen]);

	const moveNext = () => {
		if (stepIndex < steps.length - 1) setStepIndex((current) => current + 1);
		else finishIntro().catch(() => undefined);
	};

	const moveBack = () => setStepIndex((current) => Math.max(0, current - 1));
	let nextButtonLabel = 'Continue';
	if (isSaving) nextButtonLabel = 'Saving…';
	else if (stepIndex >= steps.length - 1) nextButtonLabel = 'Finish';

	if (!isOpen || !isTourPositioned) return null;

	return (
		<Portal>
			<div className='intro-tour-layer' role='dialog' aria-modal='true' aria-labelledby='intro-tour-step-title'>
				{spotlight ? (
					<>
						<div className='intro-tour-backdrop' style={{ top: 0, left: 0, right: 0, height: spotlight.top }} />
						<div className='intro-tour-backdrop' style={{ top: spotlight.top, left: 0, width: spotlight.left, height: spotlight.height }} />
						<div className='intro-tour-backdrop' style={{ top: spotlight.top, left: spotlight.right, right: 0, height: spotlight.height }} />
						<div className='intro-tour-backdrop' style={{ top: spotlight.bottom, left: 0, right: 0, bottom: 0 }} />
						<div
							className='intro-tour-spotlight'
							style={{ top: spotlight.top, left: spotlight.left, width: spotlight.width, height: spotlight.height }}
						/>
					</>
				) : (
					<div className='intro-tour-backdrop intro-tour-backdrop-full' />
				)}
				<div
					ref={popoverRef}
					className='intro-tour-popover'
					style={{ top: popoverPosition.top, left: popoverPosition.left }}
					aria-live='polite'>
					<div className='intro-tour-popover-topline'>
						<span>WELCOME, {displayName}</span>
						<span>{steps.length ? `${stepIndex + 1} / ${steps.length}` : 'READY'}</span>
					</div>
					<div className='intro-tour-progress' role='progressbar' aria-valuenow={steps.length ? stepIndex + 1 : 0} aria-valuemin={0} aria-valuemax={Math.max(steps.length, 1)}>
						<span style={{ width: `${steps.length ? ((stepIndex + 1) / steps.length) * 100 : 100}%` }} />
					</div>
					<div className='intro-tour-role'>Signed in as <strong>{roleName}</strong></div>
					<h2 id='intro-tour-step-title'>{activeStep?.label || 'Your StichMaster workspace'}</h2>
					<p className='intro-tour-description'>
						{activeStep?.description || 'Your account does not currently have any sidebar areas assigned. Ask your administrator to set up your access.'}
					</p>
					{activeStep?.tabs && activeStep.tabs.length > 0 && (
						<div className='intro-tour-tabs' aria-label='Pages available in this area'>
							{activeStep.tabs.map((tab) => <span key={tab}>{tab}</span>)}
						</div>
					)}
					<div className='intro-tour-popover-footer'>
						<button type='button' className='intro-tour-skip' onClick={skipTour} disabled={isSaving}>
							Skip tour
						</button>
						<div className='intro-tour-navigation'>
							{stepIndex > 0 && (
								<button type='button' className='btn btn-light' onClick={moveBack} disabled={isSaving}>
									Back
								</button>
							)}
							<button type='button' className='btn btn-primary' onClick={moveNext} disabled={isSaving}>
								{nextButtonLabel}
							</button>
						</div>
					</div>
				</div>
			</div>
		</Portal>
	);
};

export default IntroTourGate;
